/** Derive the effective `kind` of a construction line from its
 *  current geometry — and, when the existing name is an
 *  auto-generated one, swap it to the matching modifier bucket on
 *  every commit. Lets a polyline that gets closed appear as
 *  "Polygon", a polyline that gets a bezier handle bent into it
 *  appear as "Curve", and so on, instead of the stored `kind` being
 *  frozen at birth.
 *
 *  Same shape as `deriveRoundedCorners` / `deriveFreehandSmoothing` —
 *  a pure project-in / project-out function the commit pipeline runs
 *  once per commit, returning the same reference when nothing
 *  changed. */

import type { ConstructionLine, Project } from "./Project";
import {
  isAutoGeneratedConstructionName,
  nextConstructionName,
  projectConstructionLines,
  REGULAR_POLYGON_SHAPE_MODIFIERS,
} from "./Project";

/** True when the path data has any cubic / quadratic bezier command
 *  (case-insensitive). Used as the "this line is curvy" signal —
 *  matches the same regex `dropConstructionPrimitive` already uses
 *  for the same purpose. */
export function pathDataHasCurveTokens(pathData: string): boolean {
  return /[CcQqSsTt]/.test(pathData);
}

/** True when the path data contains any close-subpath command. A
 *  freehand subpath that closes back to its start is `M … Z`; a
 *  polygon ends `… Z`; an open polyline / open curve has no Z. */
export function pathDataHasClosedSubpath(pathData: string): boolean {
  return /[Zz]/.test(pathData);
}

/** Compute the effective kind of a construction-line from its
 *  visible `pathData`. The rules (per issue #171 follow-up):
 *  - Any bezier handle wins → "freehand" (labelled "Curve").
 *  - Otherwise: closed → "polygon"; open → "polyline".
 *
 *  Caller is responsible for skipping primitive lines (those have
 *  no kind in this set — their label is the primitive modifier) and
 *  offset-derived lines (those follow their source). */
export function effectiveConstructionLineKind(
  pathData: string,
): "polyline" | "polygon" | "freehand" {
  if (pathDataHasCurveTokens(pathData)) return "freehand";
  if (pathDataHasClosedSubpath(pathData)) return "polygon";
  return "polyline";
}

/** Display modifier for an effective kind — drives the
 *  auto-generated name bucket ("Polyline N", "Polygon N", "Curve
 *  N") and the panel header label. */
export function modifierForEffectiveKind(
  kind: "polyline" | "polygon" | "freehand",
): "Polyline" | "Polygon" | "Curve" {
  return kind === "polyline"
    ? "Polyline"
    : kind === "polygon"
      ? "Polygon"
      : "Curve";
}

/** Walk every construction line; recompute `kind` from geometry and,
 *  when the line's current name is an auto-generated one whose
 *  modifier no longer matches the new kind, swap it to a fresh name
 *  in the correct bucket. Primitive and offset-derived lines are
 *  passed through unchanged.
 *
 *  Returns the input project unchanged when no line needs an update
 *  so callers can cheaply skip the snapshot — same memoization
 *  contract as `deriveRoundedCorners`. */
export function deriveConstructionLineKinds(project: Project): Project {
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return project;
  let changed = false;
  // Build the new list incrementally so that when two lines
  // transition in the same commit, the second rename sees the
  // first one's updated name and picks a fresh counter.
  const next: ConstructionLine[] = [];
  let scratchProject = project;
  for (const line of lines) {
    const updated = applyOne(line, scratchProject);
    next.push(updated);
    if (updated !== line) {
      changed = true;
      scratchProject = {
        ...scratchProject,
        constructionLines: next.concat(lines.slice(next.length)),
      };
    }
  }
  if (!changed) return project;
  return { ...project, constructionLines: next };
}

function applyOne(line: ConstructionLine, project: Project): ConstructionLine {
  // Parametric primitives carry their identity in `primitive`; their
  // header label is `modifierFor(primitive)`, so they never enter
  // the polyline / polygon / curve bucket and `kind` stays
  // undefined for them by convention. Skip.
  if (line.primitive) return line;
  // Derived (offset) lines follow their source — their kind isn't
  // meaningful and the user can't edit them directly. Skip.
  if (line.offsetSource) return line;
  // Lines that have been rounded via `cornerRounding` carry the
  // rounded form in `pathData` (cubic beziers from
  // `applyCornerRounding`) but their AUTHORING geometry is still
  // the un-rounded `sharpPathData`. Classifying from `pathData`
  // would silently demote a rounded polygon / polyline to "Curve",
  // which is what happens to a polygon offset the moment the user
  // detaches it (issue #244 follow-up). Use the sharp form when
  // it's there so a rounded polygon stays a polygon.
  const geomForKind = line.cornerRounding?.sharpPathData ?? line.pathData;
  const effective = effectiveConstructionLineKind(geomForKind);
  const sameKind = line.kind === effective;
  // Auto-name rewrite only when the name's current modifier doesn't
  // match the new kind. Hand-typed names ("My fancy outline") are
  // left untouched even when the kind changes — `isAutoGeneratedConstructionName`
  // is what gates that.
  const newModifier = modifierForEffectiveKind(effective);
  const nameLooksAuto = isAutoGeneratedConstructionName(line.name);
  // The well-known regular-polygon shape names (Triangle … Octagon) are
  // valid buckets for a polygon-kind line — a "Hexagon 1" must not get
  // renamed to "Polygon N" by a derive pass. Once the geometry stops being
  // a polygon (e.g. the ring is opened into a polyline), they rename into
  // the new kind's bucket like any other auto name.
  const bucketModifiers =
    effective === "polygon"
      ? [newModifier, ...REGULAR_POLYGON_SHAPE_MODIFIERS]
      : [newModifier];
  const nameAlreadyInBucket =
    line.name !== undefined &&
    bucketModifiers.some(
      (mod) =>
        new RegExp(`^${mod} \\d+$`).test(line.name!) ||
        new RegExp(`^Construction - ${mod} \\d+$`).test(line.name!),
    );
  const needsRename = nameLooksAuto && !nameAlreadyInBucket;
  if (sameKind && !needsRename) return line;
  const next: ConstructionLine = { ...line, kind: effective };
  if (needsRename) {
    next.name = nextConstructionName(project, newModifier);
  }
  return next;
}
